import { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { QButton, QTable, QTableRow, QTableHead, QTableCell, QTableBody, QIconButton } from '@kw/quokka-ui';
import { useUserManagementParams } from './useUserManagementParams';
import { PageLoader } from '../../PageLoader';
import { getComparator } from '../../utils/getComparator';
import { KwColumnSortHeader } from '../../kw-ui-components/KwColumnSortHeader';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useOrg } from '../Organization/org-provider';
import { useUsersList } from './Integrations';
import { DeleteUserModal } from './DeleteUserModal';
import { InviteUserModal } from './InviteUserModal';

export const UserManagement = () => {
  const { orgData } = useOrg();
  const orgName = orgData?.name || '';

  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { data: responseData, error: responseError, isLoading, mutate } = useUsersList(accessToken);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [users, setUsers] = useState({ users: [], total: 0 });
  const [usersCount, setUsersCount] = useState(0);
  const [invitedUser, setInvitedUser] = useState(null);
  // Display a progress indicator when a user is being deleted
  const [progressDisplayed, setProgressDisplayed] = useState(false);

  useEffect(() => {
    if (responseData) {
      setUsers(responseData);
      setUsersCount(responseData.total);
    }
  }, [responseData]);

  const {
    nameOrder,
    createdDateOrder,
    lastLoginOrder,
    toggleNameOrder,
    toggleCreatedDateOrder,
    toggleLastLoginOrder,
    orderBy,
    setOrderBy,
  } = useUserManagementParams();

  const tableHeaders = [
    {
      title: 'Name',
      onClick: toggleNameOrder,
      sortValue: nameOrder,
    },
    {
      title: 'Created Date',
      onClick: toggleCreatedDateOrder,
      sortValue: createdDateOrder,
    },
    {
      title: 'Last Login',
      onClick: toggleLastLoginOrder,
      sortValue: lastLoginOrder,
    },
    {
      title: 'Actions',
      onClick: () => {},
      sortValue: null,
    },
  ];

  // column sorting UI filters
  const order = tableHeaders.map(({ sortValue }) => sortValue).find(o => o);

  useEffect(() => {
    if (nameOrder) {
      setOrderBy('name');
    }

    if (createdDateOrder) {
      setOrderBy('created_at');
    }

    if (lastLoginOrder) {
      setOrderBy('last_login');
    }

    if (!order) {
      setOrderBy(null);
    }
  }, [order, orderBy, setOrderBy, nameOrder, createdDateOrder, lastLoginOrder]);

  const [modalOptions, setModalOptions] = useState({ isOpen: false, user: {} });

  const handleDeleteUser = user => {
    setModalOptions({ isOpen: true, user });
  };

  const handleInviteUser = () => {
    setIsInviteModalOpen(true);
  };

  const formatDate = (timestamp: string): string => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  const formatDateTime = (timestamp: string): string => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    hours = hours || 12; // the hour '0' should be '12'
    const strTime = `${hours}:${minutes} ${ampm}`;
    return `${month}/${day}/${year} ${strTime}`;
  };
  // if users list fails to update after invite is first sent, keep trying until up to date
  useEffect(() => {
    if (usersCount > users.total) {
      setUsers({ users: [...users.users, invitedUser], total: usersCount });
      setTimeout(() => {
        mutate();
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersCount]);

  return (
    <>
      {isLoading && <PageLoader />}
      <>
        <div style={{ float: 'right', marginBottom: '20px' }}>
          <StyledSummary>
            {orgName} | {users.total} {users.total === 1 ? 'user' : 'users'}
          </StyledSummary>
          <QButton variant="filled" size="medium" disabled={false} onClick={event => handleInviteUser()}>
            Invite
          </QButton>
        </div>
        <StyledTableWrapper>
          <QTable aria-label="Users-Table">
            <QTableHead>
              <QTableRow>
                {tableHeaders.map(({ title, onClick, sortValue }) => (
                  <KwColumnSortHeader
                    key={title}
                    title={title}
                    onClick={() => {
                      onClick();
                    }}
                    sortValue={sortValue}
                  />
                ))}
              </QTableRow>
            </QTableHead>
            <QTableBody>
              {[...users.users].sort(getComparator(order, orderBy)).map(user => {
                const pictureUrl = user?.picture;

                return (
                  <QTableRow key={user.user_id} aria-label={`${user.name} row`}>
                    <StyledQTableCell>
                      <div style={{ display: 'grid' }}>
                        <StyledAvatar alt={`${user.name} app icon`} src={pictureUrl} />
                        <StyledNameTag>{user.name}</StyledNameTag>
                        <StyledEmailTag>
                          {user.email} {user.temp ? 'is being added' : ''}
                        </StyledEmailTag>
                      </div>
                    </StyledQTableCell>
                    <QTableCell>{formatDate(user.created_at)}</QTableCell>
                    <QTableCell>{formatDateTime(user.last_login)}</QTableCell>
                    <QTableCell>
                      {!user.temp ? (
                        <QIconButton size="medium" disabled={false} onClick={() => handleDeleteUser(user)}>
                          <DeleteOutlineIcon />
                        </QIconButton>
                      ) : null}
                    </QTableCell>
                  </QTableRow>
                );
              })}
            </QTableBody>
          </QTable>
        </StyledTableWrapper>
        <DeleteUserModal
          isOpen={modalOptions.isOpen}
          onCancel={() => setModalOptions({ isOpen: false, user: {} })}
          onDelete={async () => {
            setModalOptions({ isOpen: false, user: {} });
            setProgressDisplayed(true);

            // Refresh the list of users after deletion. Auth0 takes a seconds to update the list of users.
            // Hence I'm using a timeout to refresh the list of users after 2 seconds.
            setTimeout(async () => {
              mutate();
              setProgressDisplayed(false);
            }, 2000);
          }}
          user={modalOptions.user}
        />
        <InviteUserModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onInviteSent={() => {
            mutate();
          }}
          setUsersCount={setUsersCount}
          setInvitedUser={setInvitedUser}
        />
        <StyledBackDrop open={progressDisplayed} onClick={() => setProgressDisplayed(false)}>
          <CircularProgress color="inherit" />
        </StyledBackDrop>
      </>
    </>
  );
};

const StyledBackDrop = styled(Backdrop)(({ theme: muiTheme }) => ({
  color: '#fff',
  zIndex: muiTheme.zIndex.drawer + 1,
}));

const StyledTableWrapper = styled('div')`
  width: 100%;
`;

const StyledQTableCell = styled(QTableCell)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledAvatar = styled(Avatar)`
  margin-right: 10px;
  grid-column-start: 1;
  grid-column-end: 1;
  grid-row-start: 1;
  grid-row-end: 3;
`;

const StyledNameTag = styled('div')`
  font-size: 18px;
  line-height: 25px;
  font-weight: 600;
  grid-column-start: 2;
  grid-column-end: 2;
  grid-row-start: 1;
  grid-row-end: 1;
`;
const StyledEmailTag = styled('div')`
  grid-column-start: 2;
  grid-column-end: 2;
  grid-row-start: 2;
  grid-row-end: 2;
  color: #757575;
  font-size: 14px;
  line-height: 21px;
  font-weight: 400;
`;

const StyledSummary = styled('span')`
  font-size: 18px;
  line-height: 25px;
  color: #757575;
  font-weight: 400;
  margin-right: 20px;
`;
